import React, { useState } from 'react';
import { ContentData } from '../data/contentData';

interface ContactPageProps {
  content: ContentData;
}

interface ContactForm {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  category: string;
}

const ContactPage: React.FC<ContactPageProps> = ({ content }) => {
  const [formData, setFormData] = useState<ContactForm>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    category: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        category: 'general'
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }, 2000);
  };

  const contactInfo = {
    address: {
      full: 'রাজউক কলেজ, ঢাকা-১০০০, বাংলাদেশ',
      street: 'ধানমন্ডি, ঢাকা-১২০৫',
      area: 'ধানমন্ডি',
      city: 'ঢাকা',
      postal: '১২০৫',
      country: 'বাংলাদেশ'
    },
    phone: {
      main: '+৮৮০-২-৯৬৬-০০১১',
      admission: '+৮৮০-২-৯৬৬-০০১২',
      principal: '+৮৮০-২-৯৬৬-০০১৩',
      emergency: '+৮৮০-১৭১১-২৩৪-৫৬৭'
    },
    email: {
      main: 'info@rajukcollege.edu.bd',
      admission: 'admission@rajukcollege.edu.bd',
      principal: 'principal@rajukcollege.edu.bd',
      support: 'support@rajukcollege.edu.bd'
    },
    hours: {
      weekdays: 'সকাল ৮:০০ - বিকাল ৫:০০',
      friday: 'সকাল ৮:০০ - দুপুর ১২:৩০',
      saturday: 'সকাল ৯:০০ - দুপুর ১:০০',
      sunday: 'বন্ধ'
    },
    social: {
      facebook: 'https://facebook.com/rajukcollege',
      whatsapp: 'https://wa.me/8801711234567',
      linkedin: 'https://linkedin.com/company/rajukcollege',
      instagram: 'https://instagram.com/rajukcollege'
    }
  };


  const categories = [
    { value: 'general', label: 'সাধারণ প্রশ্ন' },
    { value: 'admission', label: 'ভর্তি সংক্রান্ত' },
    { value: 'academic', label: 'শিক্ষা সংক্রান্ত' },
    { value: 'exam', label: 'পরীক্ষা সংক্রান্ত' },
    { value: 'result', label: 'ফলাফল সংক্রান্ত' },
    { value: 'fee', label: 'ফি সংক্রান্ত' },
    { value: 'complaint', label: 'অভিযোগ' },
    { value: 'suggestion', label: 'পরামর্শ' }
  ];

  return (
    <div className="py-16 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4 bangla-font">{content.contact.title}</h1>
          <p className="text-lg text-gray-600 bangla-font">আমাদের সাথে যোগাযোগ করুন</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className="space-y-8">
            {/* Main Contact Info */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 bangla-font">যোগাযোগের তথ্য</h2>
              
              {/* Address */}
              <div className="mb-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 text-xl">📍</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 bangla-font">ঠিকানা</h3>
                    <p className="text-gray-600 bangla-font">{contactInfo.address.full}</p>
                    <p className="text-sm text-gray-500 bangla-font mt-1">
                      {contactInfo.address.street}, {contactInfo.address.city}-{contactInfo.address.postal}
                    </p>
                  </div>
                </div>
              </div>

              {/* Phone Numbers */}
              <div className="mb-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-green-600 text-xl">📞</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 bangla-font">ফোন নম্বর</h3>
                    <div className="space-y-1">
                      <p className="text-gray-600 bangla-font">প্রধান: {contactInfo.phone.main}</p>
                      <p className="text-sm text-gray-500 bangla-font">ভর্তি: {contactInfo.phone.admission}</p>
                      <p className="text-sm text-gray-500 bangla-font">অধ্যক্ষ: {contactInfo.phone.principal}</p>
                      <p className="text-sm text-gray-500 bangla-font">জরুরি: {contactInfo.phone.emergency}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Addresses */}
              <div className="mb-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <span className="text-purple-600 text-xl">✉️</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 bangla-font">ইমেইল</h3>
                    <div className="space-y-1">
                      <p className="text-gray-600 bangla-font">প্রধান: {contactInfo.email.main}</p>
                      <p className="text-sm text-gray-500 bangla-font">ভর্তি: {contactInfo.email.admission}</p>
                      <p className="text-sm text-gray-500 bangla-font">অধ্যক্ষ: {contactInfo.email.principal}</p>
                      <p className="text-sm text-gray-500 bangla-font">সহায়তা: {contactInfo.email.support}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Office Hours */}
              <div className="mb-6">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-orange-600 text-xl">🕒</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2 bangla-font">অফিসের সময়</h3>
                    <div className="space-y-1">
                      <p className="text-gray-600 bangla-font">সোম-বৃহ: {contactInfo.hours.weekdays}</p>
                      <p className="text-sm text-gray-500 bangla-font">শুক্রবার: {contactInfo.hours.friday}</p>
                      <p className="text-sm text-gray-500 bangla-font">শনিবার: {contactInfo.hours.saturday}</p>
                      <p className="text-sm text-gray-500 bangla-font">রবিবার: {contactInfo.hours.sunday}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>


            {/* Social Media */}
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 bangla-font">সামাজিক যোগাযোগ</h2>
              <div className="grid grid-cols-2 gap-4">
                <a
                  href={contactInfo.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <span className="text-2xl">📘</span>
                  <span className="bangla-font">ফেসবুক</span>
                </a>
                <a
                  href={contactInfo.social.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <span className="text-2xl">💬</span>
                  <span className="bangla-font">হোয়াটসঅ্যাপ</span>
                </a>
                <a
                  href={contactInfo.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-3 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
                >
                  <span className="text-2xl">💼</span>
                  <span className="bangla-font">লিংকডইন</span>
                </a>
                <a
                  href={contactInfo.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-3 p-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition-colors"
                >
                  <span className="text-2xl">📷</span>
                  <span className="bangla-font">ইনস্টাগ্রাম</span>
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 bangla-font">বার্তা পাঠান</h2>
            
            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg bangla-font">
                ✅ আপনার বার্তা সফলভাবে পাঠানো হয়েছে। আমরা শীঘ্রই আপনার সাথে যোগাযোগ করব।
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg bangla-font">
                ❌ বার্তা পাঠাতে সমস্যা হয়েছে। অনুগ্রহ করে আবার চেষ্টা করুন।
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2 bangla-font">
                    নাম *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bangla-font"
                    placeholder="আপনার পূর্ণ নাম"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 bangla-font">
                    ইমেইল *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2 bangla-font">
                    ফোন নম্বর
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bangla-font"
                    placeholder="+৮৮০-১৭১১-২৩৪-৫৬৭"
                  />
                </div>
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2 bangla-font">
                    বিষয় *
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bangla-font"
                  >
                    {categories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2 bangla-font">
                  বিষয় *
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bangla-font"
                  placeholder="বার্তার বিষয়"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2 bangla-font">
                  বার্তা *
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bangla-font"
                  placeholder="আপনার বার্তা লিখুন..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                  isSubmitting
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } bangla-font`}
              >
                {isSubmitting ? 'পাঠানো হচ্ছে...' : 'বার্তা পাঠান'}
              </button>
            </form>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-16 bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center bangla-font">আমাদের অবস্থান</h2>
          <div className="rounded-lg overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.0!2d90.3885!3d23.7500!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDQ1JzAwLjAiTiA5MMKwMjMnMTguNiJF!5e0!3m2!1sen!2sbd!4v1234567890123!5m2!1sen!2sbd"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="রাজউক কলেজের অবস্থান"
            ></iframe>
          </div>
          <div className="mt-4 text-center">
            <p className="text-gray-600 bangla-font">ধানমন্ডি, ঢাকা-১২০৫, বাংলাদেশ</p>
            <p className="text-sm text-gray-500 bangla-font mt-2">
              ভবিষ্যতে সঠিক অবস্থান আপডেট করা হবে
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
